import React, { useState, useEffect, useCallback } from 'react';
import { Plug, Youtube, ExternalLink, Check, X, Loader2, RefreshCw, Users, Zap } from 'lucide-react';
import VideoIntegrationService from '../../../services/videoIntegrationService';
import GhlIntegrationService from '../../../services/ghlIntegrationService';
import ZapierIntegrationService from '../../../services/zapierIntegrationService';
import { useSiteContext } from '../../../context/SiteContext';
import { toast } from 'react-toastify';

// Per-user gate: only these accounts currently see the GoHighLevel integration.
// Backend endpoints are unaffected — they're per-user already.
const GHL_ALLOWED_EMAILS = new Set([
  'krishan.kothari@gmail.com',
  'chris@r-link.com',
]);

// Per-user gate for the Zapier card. Backend endpoints stay per-user via OAuth.
const ZAPIER_ALLOWED_EMAILS = new Set([
  'krishan.kothari@gmail.com',
]);

// REACT_APP_ZAPIER_CONNECT_URL is the public invite/install URL printed by
// `zapier users:links` after pushing the rlink-zapier-app. While the app is
// still private (not on the public marketplace), the marketplace URL 404s —
// fall back to the developer console as a safer default.
const ZAPIER_APP_URL =
  process.env.REACT_APP_ZAPIER_CONNECT_URL ||
  'https://developer.zapier.com/';
const ZAPIER_MANAGE_URL = 'https://zapier.com/app/zaps';

const PRIVACY_OPTIONS_YT = [
  { value: 'public', label: 'Public' },
  { value: 'unlisted', label: 'Unlisted' },
  { value: 'private', label: 'Private' },
];

const PRIVACY_OPTIONS_VIMEO = [
  { value: 'anybody', label: 'Public' },
  { value: 'unlisted', label: 'Unlisted (hidden from Vimeo)' },
  { value: 'nobody', label: 'Private (only me)' },
];

const VideoIntegrationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [youtube, setYoutube] = useState(null);
  const [vimeo, setVimeo] = useState(null);
  const [saving, setSaving] = useState(null);

  // GHL state (only used when the email gate below allows it)
  const { user } = useSiteContext();
  const ghlAllowed = GHL_ALLOWED_EMAILS.has(
    (user?.email || '').toLowerCase()
  );
  const zapierAllowed = ZAPIER_ALLOWED_EMAILS.has(
    (user?.email || '').toLowerCase()
  );
  const [ghl, setGhl] = useState(null);
  const [ghlApiKey, setGhlApiKey] = useState('');
  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlConnecting, setGhlConnecting] = useState(false);

  // Zapier state (only used when the Zapier email gate above allows it)
  const [zapier, setZapier] = useState(null);
  const [zapierRevoking, setZapierRevoking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [vidRes, ghlRes, zapierRes] = await Promise.all([
        VideoIntegrationService.getStatus().catch(() => ({ success: false })),
        ghlAllowed
          ? GhlIntegrationService.getStatus().catch(() => ({ success: false }))
          : Promise.resolve({ success: false }),
        zapierAllowed
          ? ZapierIntegrationService.getStatus().catch(() => ({ success: false }))
          : Promise.resolve({ success: false }),
      ]);
      if (vidRes.success) {
        setEnabled(vidRes.enabled);
        if (vidRes.enabled) {
          setYoutube(vidRes.youtube);
          setVimeo(vidRes.vimeo);
        }
      }
      if (ghlAllowed && ghlRes.success) setGhl(ghlRes);
      if (zapierAllowed && zapierRes.success) setZapier(zapierRes);
    } catch (err) {
      console.error('Failed to load integration status:', err);
    } finally {
      setLoading(false);
    }
  }, [ghlAllowed, zapierAllowed]);

  const handleGhlConnect = async () => {
    if (!ghlApiKey.trim() || !ghlLocationId.trim()) {
      toast.error('Both Private Integration Token and Location ID are required.');
      return;
    }
    setGhlConnecting(true);
    try {
      const res = await GhlIntegrationService.connect({
        apiKey: ghlApiKey.trim(),
        locationId: ghlLocationId.trim(),
      });
      if (res.success) {
        toast.success(`GHL connected — ${res.locationName || res.locationId}`);
        setGhlApiKey('');
        setGhlLocationId('');
        fetchStatus();
      } else {
        toast.error(res.message || 'Failed to connect GHL');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to connect GHL';
      toast.error(msg);
    } finally {
      setGhlConnecting(false);
    }
  };

  const handleGhlDisconnect = async () => {
    try {
      await GhlIntegrationService.disconnect();
      toast.success('GoHighLevel disconnected');
      fetchStatus();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const handleZapierConnect = () => {
    window.open(ZAPIER_APP_URL, '_blank', 'noopener,noreferrer');
  };

  const handleZapierRevoke = async () => {
    setZapierRevoking(true);
    try {
      const res = await ZapierIntegrationService.revokeAll();
      const count = res?.revoked ?? 0;
      toast.success(
        count > 0
          ? `Revoked ${count} Zapier connection${count === 1 ? '' : 's'}`
          : 'No active Zapier connections'
      );
      fetchStatus();
    } catch (err) {
      toast.error('Failed to revoke Zapier connections');
    } finally {
      setZapierRevoking(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Listen for OAuth popup messages
  useEffect(() => {
    const handler = (event) => {
      const { type } = event.data || {};
      if (type === 'youtube-connected' || type === 'vimeo-connected') {
        toast.success(`${type.includes('youtube') ? 'YouTube' : 'Vimeo'} connected successfully`);
        fetchStatus();
      }
      if (type === 'youtube-error' || type === 'vimeo-error') {
        toast.error(`Connection failed: ${event.data.error}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchStatus]);

  const handleConnect = async (platform) => {
    try {
      const res = platform === 'youtube'
        ? await VideoIntegrationService.getYouTubeAuthUrl()
        : await VideoIntegrationService.getVimeoAuthUrl();
      if (res.success && res.url) {
        const w = 600, h = 700;
        const left = window.screenX + (window.innerWidth - w) / 2;
        const top = window.screenY + (window.innerHeight - h) / 2;
        window.open(res.url, `${platform}-auth`, `width=${w},height=${h},left=${left},top=${top}`);
      }
    } catch (err) {
      toast.error(`Failed to start ${platform} connection`);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      const fn = platform === 'youtube'
        ? VideoIntegrationService.disconnectYouTube
        : VideoIntegrationService.disconnectVimeo;
      await fn();
      toast.success(`${platform === 'youtube' ? 'YouTube' : 'Vimeo'} disconnected`);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSettingChange = async (platform, key, value) => {
    setSaving(`${platform}-${key}`);
    try {
      await VideoIntegrationService.updateSettings({ platform, [key]: value });
      // Update local state
      const setter = platform === 'youtube' ? setYoutube : setVimeo;
      setter(prev => ({ ...prev, [key]: value }));
    } catch (err) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // GHL card — only rendered when the email gate allows it.
  const ghlCard = ghlAllowed ? (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
          <Users className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-white font-semibold">GoHighLevel</h2>
          <p className="text-sm text-gray-400">Auto-export new leads to your GHL contacts with meeting tags</p>
        </div>
      </div>

      {ghl?.connected ? (
        <>
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
            <Check className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-400">
              Connected to {ghl.locationName || ghl.locationId}
            </span>
          </div>

          <p className="text-xs text-gray-400 mb-4">
            New leads from your meetings & webinars are pushed to this location with two tags:{' '}
            <code className="text-gray-300">Meeting: &lt;name&gt;</code> and{' '}
            <code className="text-gray-300">Meeting Date: &lt;YYYY-MM-DD&gt;</code>.
          </p>

          <button
            onClick={handleGhlDisconnect}
            className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-400 text-sm transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Disconnect GoHighLevel
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-4">
            Paste a Private Integration Token (GHL → Settings → Private Integrations) with{' '}
            <code className="text-gray-300">contacts.write</code> scope, plus your Location ID. Leads
            are only exported to GoHighLevel while this integration is connected.
          </p>

          <label className="block text-xs text-gray-400 mb-1">Private Integration Token</label>
          <input
            type="password"
            value={ghlApiKey}
            onChange={(e) => setGhlApiKey(e.target.value)}
            placeholder="pit-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full mb-3 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-purple-500/60"
          />

          <label className="block text-xs text-gray-400 mb-1">Location ID</label>
          <input
            type="text"
            value={ghlLocationId}
            onChange={(e) => setGhlLocationId(e.target.value)}
            placeholder="e.g. hEkiQAMFPC3B1YtZwfqk"
            className="w-full mb-4 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-purple-500/60"
          />

          <button
            onClick={handleGhlConnect}
            disabled={ghlConnecting}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-60 rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
          >
            {ghlConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            {ghlConnecting ? 'Validating…' : 'Connect GoHighLevel'}
          </button>
        </>
      )}
    </div>
  ) : null;

  // Zapier card — only rendered when the Zapier email gate allows it.
  const zapierConnections = zapier?.connectionsCount || 0;
  const zapierConnected = zapierConnections > 0;
  const zapierCard = zapierAllowed ? (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6a1fbf]/30 to-[#00c853]/30 flex items-center justify-center">
          <Zap className="w-5 h-5 text-[#ff9500]" />
        </div>
        <div>
          <h2 className="text-white font-semibold">Zapier</h2>
          <p className="text-sm text-gray-400">Trigger Zaps from meetings, recordings, registrants, and signed contracts</p>
        </div>
      </div>

      {zapierConnected ? (
        <>
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
            <Check className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-400">
              {zapierConnections} active connection{zapierConnections === 1 ? '' : 's'}
            </span>
          </div>

          <p className="text-xs text-gray-400 mb-4">
            Manage individual Zaps from your Zapier dashboard. Revoking here disconnects every Zap that uses your R-Link account.
          </p>

          <a
            href={ZAPIER_MANAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white text-sm transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Manage in Zapier
          </a>

          <button
            onClick={handleZapierRevoke}
            disabled={zapierRevoking}
            className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-400 text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {zapierRevoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            {zapierRevoking ? 'Revoking…' : 'Revoke all connections'}
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-4">
            Click below to open R-Link Studio in the Zapier app marketplace, then add a connection — Zapier will redirect you back here to authorize.
          </p>
          <button
            onClick={handleZapierConnect}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Connect Zapier
          </button>
        </>
      )}
    </div>
  ) : null;

  if (!enabled) {
    return (
      <div className="p-4 md:p-0">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Integrations</h1>
          <p className="text-gray-400">Connect third-party services to your R-Link Studio account.</p>
        </div>

        {(ghlCard || zapierCard) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {ghlCard}
            {zapierCard}
          </div>
        )}

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
          <Plug className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h2 className="text-lg text-white font-medium mb-2">Video Integrations Not Available</h2>
          <p className="text-gray-400">Contact your administrator to enable YouTube and Vimeo integrations for your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-0">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Integrations</h1>
        <p className="text-gray-400">
          {ghlCard
            ? 'Connect YouTube, Vimeo, and GoHighLevel to your R-Link Studio account.'
            : 'Connect YouTube and Vimeo to automatically upload your recordings.'}
        </p>
      </div>

      {(ghlCard || zapierCard) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {ghlCard}
          {zapierCard}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* YouTube Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Youtube className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-white font-semibold">YouTube</h2>
              <p className="text-sm text-gray-400">Upload recordings to your YouTube channel</p>
            </div>
          </div>

          {youtube?.connected ? (
            <>
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-400">Connected to {youtube.channelName || 'YouTube'}</span>
              </div>

              {/* Auto-upload toggle */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-white font-medium">Auto-upload recordings</p>
                  <p className="text-xs text-gray-400">Automatically upload every new recording</p>
                </div>
                <button
                  onClick={() => handleSettingChange('youtube', 'autoUpload', !youtube.autoUpload)}
                  disabled={saving === 'youtube-autoUpload'}
                  className={`relative w-11 h-6 rounded-full transition-colors ${youtube.autoUpload ? 'bg-purple-600' : 'bg-white/20'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${youtube.autoUpload ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {/* Default privacy */}
              <div className="mb-4">
                <p className="text-sm text-white font-medium mb-2">Default privacy</p>
                <select
                  value={youtube.defaultPrivacy || 'unlisted'}
                  onChange={(e) => handleSettingChange('youtube', 'defaultPrivacy', e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-purple-500/60"
                >
                  {PRIVACY_OPTIONS_YT.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => handleDisconnect('youtube')}
                className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-400 text-sm transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Disconnect YouTube
              </button>
            </>
          ) : (
            <button
              onClick={() => handleConnect('youtube')}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Connect YouTube Account
            </button>
          )}
        </div>

        {/* Vimeo Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 3.501-3.123C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-white font-semibold">Vimeo</h2>
              <p className="text-sm text-gray-400">Upload recordings to your Vimeo account</p>
            </div>
          </div>

          {vimeo?.connected ? (
            <>
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-400">Connected to {vimeo.accountName || 'Vimeo'}</span>
              </div>

              {/* Auto-upload toggle */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-white font-medium">Auto-upload recordings</p>
                  <p className="text-xs text-gray-400">Automatically upload every new recording</p>
                </div>
                <button
                  onClick={() => handleSettingChange('vimeo', 'autoUpload', !vimeo.autoUpload)}
                  disabled={saving === 'vimeo-autoUpload'}
                  className={`relative w-11 h-6 rounded-full transition-colors ${vimeo.autoUpload ? 'bg-purple-600' : 'bg-white/20'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${vimeo.autoUpload ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {/* Default privacy */}
              <div className="mb-4">
                <p className="text-sm text-white font-medium mb-2">Default privacy</p>
                <select
                  value={vimeo.defaultPrivacy || 'unlisted'}
                  onChange={(e) => handleSettingChange('vimeo', 'defaultPrivacy', e.target.value)}
                  className="w-full px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-purple-500/60"
                >
                  {PRIVACY_OPTIONS_VIMEO.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => handleDisconnect('vimeo')}
                className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg text-red-400 text-sm transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Disconnect Vimeo
              </button>
            </>
          ) : (
            <button
              onClick={() => handleConnect('vimeo')}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Connect Vimeo Account
            </button>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="mt-6 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
        <h3 className="text-white font-medium mb-2">How it works</h3>
        <ul className="text-sm text-gray-400 space-y-1.5">
          <li>1. Connect your YouTube or Vimeo account above</li>
          <li>2. Enable auto-upload, or manually upload individual recordings from the Recordings page</li>
          <li>3. Recordings are always saved to R-Link first — platform uploads happen after</li>
          <li>4. If an upload fails, you can retry it anytime from the recording detail page</li>
        </ul>
      </div>
    </div>
  );
};

export default VideoIntegrationSettings;
